import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from "@syncfusion/ej2-react-grids";

import EmptyData from "../../../components/EmptyData";
import { ToastComponent } from "../../Components/ToastComponent";
import { clientService } from "../../../Services/client.service";
import { paginationService } from "../../../Services/pagination.service";

// Datas vêm como ISO completo com timezone (ex.: "2026-06-09T18:02:11.007-03:00"),
// não "YYYY-MM-DD" — new Date() lida com o offset certo, e getDate/getMonth/
// getFullYear pegam os componentes já no horário local do navegador.
const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDoc = (doc) => {
  const digits = (doc ?? "").toString().replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return doc ?? "";
};

// Aba "Ativos" do cliente (issue #504) — busca todos os contratos ativos
// vinculados ao cliente do usuário autenticado via GET
// /api/v3/customers/contracts/active e mostra só a lista, sem formulário de
// busca/filtro na frente (removido — a aba deve exibir os contratos
// diretamente assim que carregarem). "Detalhes" ainda não tem tela própria,
// então só mostra um toast avisando que está em desenvolvimento.
export default function Assets() {
  const { t } = useTranslation();

  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [toastParams, setToastParams] = useState("");

  // Altura e paginação dinâmicos do grid (mesmo padrão de CustomerSearch.jsx)
  const [pageSize, setPageSize] = useState(15);
  const gridAnchorRef = useRef(null);

  const ROW_HEIGHT = 36;
  // gridAnchorRef já mede o "top" real do grid, então esse budget cobre só o
  // que fica ABAIXO dele — cabeçalho + pager do próprio grid Syncfusion.
  const HEADER_PAGER_BUDGET = 70;
  const BOTTOM_MARGIN = 134;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const calculateGridMetrics = () => {
    paginationService.calculateGridMetrics(
      gridAnchorRef,
      BOTTOM_MARGIN,
      HEADER_PAGER_BUDGET,
      ROW_HEIGHT,
      MIN_PAGE,
      MAX_PAGE,
      contracts,
      window.innerHeight,
      setPageSize
    );
  };

  useEffect(() => {
    if (contracts.length === 0) return undefined;

    calculateGridMetrics();

    let resizeTimeout = null;
    const onResize = () => {
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(calculateGridMetrics, 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts]);

  useEffect(() => {
    let isMounted = true;

    const loadActiveContracts = async () => {
      setLoadingContracts(true);
      const data = await clientService.fetchActiveContracts();
      if (!isMounted) return;

      if (data) {
        // Ajustar aqui se o backend vier num envelope (ex: data.items)
        // em vez de array puro — confirmar no Network tab.
        setContracts(Array.isArray(data) ? data : data.items ?? []);
      } else {
        setContracts([]);
        showToast(
          t("CUSTOMER.ASSETS.LOAD_ERROR_TITLE"),
          t("CUSTOMER.ASSETS.LOAD_ERROR_BODY"),
          "Danger"
        );
      }

      setLoadingContracts(false);
    };

    loadActiveContracts();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowDetails = (contract) => {
    showToast(
      t("CUSTOMER.ASSETS.DETAILS_TITLE"),
      t("CUSTOMER.ASSETS.DETAILS_WIP", { contractId: contract.contractId }),
      "Warning"
    );
  };

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      {loadingContracts ? (
        <p className="text-muted mb-0">{t("COMMON.LOADING")}</p>
      ) : contracts.length === 0 ? (
        <EmptyData />
      ) : (
        <>
          <div ref={gridAnchorRef} />
          <GridComponent
            dataSource={contracts}
            allowPaging
            pageSettings={{ pageSize }}
            rowHeight={ROW_HEIGHT}
            height="auto"
            actionComplete={calculateGridMetrics}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="contractId"
                headerText={t("CUSTOMER.ASSETS.COLUMN_CONTRACT")}
                width="100"
              />
              <ColumnDirective
                field="licensePlate"
                headerText={t("CUSTOMER.ASSETS.COLUMN_PLATE")}
                width="100"
                template={(row) => <span title={row.plateTooltip || undefined}>{row.licensePlate}</span>}
              />
              <ColumnDirective
                field="baseDate"
                headerText={t("CUSTOMER.ASSETS.COLUMN_REGISTERED_AT")}
                width="120"
                template={(row) => <span>{formatDate(row.baseDate)}</span>}
              />
              <ColumnDirective
                field="activationDate"
                headerText={t("CUSTOMER.ASSETS.COLUMN_ACTIVATED_AT")}
                width="120"
                template={(row) => <span>{formatDate(row.activationDate)}</span>}
              />
              <ColumnDirective
                field="lastModifiedDate"
                headerText={t("CUSTOMER.ASSETS.COLUMN_LAST_CHANGE_AT")}
                width="130"
                template={(row) => <span>{formatDate(row.lastModifiedDate)}</span>}
              />
              <ColumnDirective field="contractStatus" headerText={t("CUSTOMER.ASSETS.COLUMN_STATUS")} width="100" />
              <ColumnDirective
                field="payerDocument"
                headerText={t("COMMON.CPF_CNPJ")}
                width="150"
                template={(row) => <span>{formatDoc(row.payerDocument)}</span>}
              />
              <ColumnDirective
                field="payerName"
                headerText={t("CUSTOMER.ASSETS.COLUMN_PAYER_NAME")}
                width="220"
              />
              <ColumnDirective
                field="commercialCondition"
                headerText={t("CUSTOMER.ASSETS.COLUMN_COMMERCIAL_CONDITION")}
                width="240"
              />
              <ColumnDirective
                headerText={t("CUSTOMER.ASSETS.COLUMN_ACTIONS")}
                width="140"
                textAlign="Center"
                template={(row) => (
                  <div className="py-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => handleShowDetails(row)}
                    >
                      {t("CUSTOMER.ASSETS.DETAILS_BUTTON")}
                    </button>
                  </div>
                )}
              />
            </ColumnsDirective>
            <Inject services={[Page]} />
          </GridComponent>
        </>
      )}
    </div>
  );
}
